import { tagByIdQuery, tagCreate, tagUpdate } from '@/entities/tags'
import type { CreateOrUpdateTagModel, TagModel } from '@/shared/api'
import { createModalModel } from '@/shared/lib/model-factories'
import { createEvent, sample } from 'effector'
import { createGate } from 'effector-react'
import { or } from 'patronum'

// Initialize

type TagFormGateProps = {
  isModal?: boolean
  onSubmitted?: (id: number) => void
  resetForm?: () => void
}

export const TagFormGate = createGate<TagFormGateProps>()

// Modal

export const {
  modalOpened: tagFormModalOpened,
  modalClosed: tagFormModalClosed,
  $isModalOpened: $isTagFormModalOpened,
} = createModalModel()

const tagFormModalOpenedForEdit = createEvent<{ id: number }>()
sample({
  clock: tagFormModalOpened,
  filter: (params): params is { id: number } => Boolean(params),
  target: tagFormModalOpenedForEdit,
})

sample({
  clock: tagFormModalOpenedForEdit,
  target: tagByIdQuery.start,
})
sample({
  source: TagFormGate.state,
  clock: tagFormModalClosed,
  fn: (state) => {
    state.resetForm?.()
  },
})

sample({
  source: tagByIdQuery.$data,
  clock: tagFormModalClosed,
  target: tagByIdQuery.reset,
})
// Submit

export const $submittingTag = or(tagCreate.$pending, tagUpdate.$pending)

export const tagSubmitted = createEvent<CreateOrUpdateTagModel>()
sample({
  source: tagByIdQuery.$data,
  clock: tagSubmitted,
  filter: (tag: TagModel | null): tag is null => tag === null,
  fn: (
    _,
    tagSubmittedData,
  ): {
    data: CreateOrUpdateTagModel
  } => {
    return { data: tagSubmittedData }
  },
  target: tagCreate.start,
})

sample({
  source: tagByIdQuery.$data,
  clock: tagSubmitted,
  filter: (tag: TagModel | null): tag is TagModel =>
    tag !== null && typeof tag.id === 'number',
  fn: (tag, tagSubmittedData) => {
    return {
      id: tag.id,
      data: tagSubmittedData,
    }
  },
  target: tagUpdate.start,
})

// OnSubmitted

sample({
  clock: [tagCreate.finished.success, tagUpdate.finished.success],
  source: TagFormGate.state,
  filter: (gateState) => Boolean(gateState.isModal),
  fn: (gateState, { result }) => {
    if (result?.id && gateState.onSubmitted) {
      gateState.onSubmitted(result.id)
    }
  },
  target: tagFormModalClosed,
})
